// ============================================================
// CLIENTE API - Axios + JWT
// ============================================================

import axios, { AxiosError } from 'axios';
import type {
  Agent, Task, User, LoginResponse, DashboardStats,
  ApiResponse, TaskFilters, CreateTaskForm
} from '../types';

// En desarrollo Vite proxea /api → 5001. En producción apuntamos directo.
const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env['VITE_API_URL'] ?? '/api';

const http = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Inyectar token en cada request
http.interceptors.request.use((config) => {
  const stored = localStorage.getItem('auth-storage');
  if (stored) {
    try {
      const { state } = JSON.parse(stored) as { state: { token: string | null } };
      if (state.token) {
        config.headers['Authorization'] = `Bearer ${state.token}`;
      }
    } catch {
      // ignore parse errors
    }
  }
  return config;
});

// Redirigir a login si el token expira
http.interceptors.response.use(
  res => res,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth-storage');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ─── Helpers ───────────────────────────────────────────────
function extractData<T>(response: { data: ApiResponse<T> }): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error ?? 'Error desconocido');
  }
  return response.data.data;
}

// ─── Auth ──────────────────────────────────────────────────
export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const res = await http.post<ApiResponse<LoginResponse>>('/auth/login', { email, password });
    return extractData(res);
  },
  logout: async (): Promise<void> => {
    await http.post('/auth/logout').catch(() => {});
  },
  me: async (): Promise<User> => {
    const res = await http.get<ApiResponse<User>>('/auth/me');
    return extractData(res);
  },
};

// ─── Agentes ──────────────────────────────────────────────
export interface AgentFormData {
  name: string;
  description: string;
  systemPrompt: string;
  avatar: string;
  color: string;
  tags: string[];
}

export const agentsApi = {
  getAll: async (): Promise<Agent[]> => {
    const res = await http.get<ApiResponse<Agent[]>>('/agents');
    return extractData(res);
  },
  getById: async (id: string): Promise<Agent> => {
    const res = await http.get<ApiResponse<Agent>>(`/agents/${id}`);
    return extractData(res);
  },
  create: async (data: AgentFormData): Promise<Agent> => {
    const res = await http.post<ApiResponse<Agent>>('/agents', data);
    return extractData(res);
  },
  update: async (id: string, data: Partial<AgentFormData>): Promise<Agent> => {
    const res = await http.put<ApiResponse<Agent>>(`/agents/${id}`, data);
    return extractData(res);
  },
  delete: async (id: string): Promise<void> => {
    await http.delete(`/agents/${id}`);
  },
  getTasks: async (id: string): Promise<Task[]> => {
    const res = await http.get<ApiResponse<Task[]>>(`/agents/${id}/tasks`);
    return extractData(res);
  },
  getDashboard: async (id: string) => {
    const res = await http.get(`/agents/${id}/dashboard`);
    return extractData(res as { data: ApiResponse<unknown> });
  },
};

// ─── Tareas ────────────────────────────────────────────────
export const tasksApi = {
  getAll: async (filters?: TaskFilters): Promise<{ tasks: Task[]; meta: ApiResponse['meta'] }> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) {
          params.set(k, String(v));
        }
      });
    }
    const res = await http.get<ApiResponse<Task[]>>(`/tasks?${params.toString()}`);
    if (!res.data.success || !res.data.data) throw new Error(res.data.error);
    return { tasks: res.data.data, meta: res.data.meta };
  },
  getById: async (id: string): Promise<Task> => {
    const res = await http.get<ApiResponse<Task>>(`/tasks/${id}`);
    return extractData(res);
  },
  create: async (data: CreateTaskForm): Promise<Task> => {
    const res = await http.post<ApiResponse<Task>>('/tasks', data);
    return extractData(res);
  },
  update: async (id: string, data: Partial<CreateTaskForm & { status: string }>): Promise<Task> => {
    const res = await http.put<ApiResponse<Task>>(`/tasks/${id}`, data);
    return extractData(res);
  },
  delete: async (id: string): Promise<void> => {
    await http.delete(`/tasks/${id}`);
  },
  execute: async (id: string): Promise<Task> => {
    const res = await http.patch<ApiResponse<Task>>(`/tasks/${id}/execute`);
    return extractData(res);
  },
};

// ─── Reportes ─────────────────────────────────────────────
// ─── DTF ──────────────────────────────────────────────────
export const dtfApi = {
  buscarCliente: async (tel: string) => {
    const res = await http.get(`/dtf/clientes/buscar?tel=${encodeURIComponent(tel)}`);
    return (res.data as ApiResponse<unknown>).data as {
      encontrado: boolean;
      cliente: { telefono: string; nombre: string; lugar: string };
      precios: Record<string, number>;
      precioEspecial: boolean;
    };
  },
  getClientesStats: async (filters?: { ano?: string; mes?: string }) => {
    const params = new URLSearchParams();
    if (filters?.ano) params.set('ano', filters.ano);
    if (filters?.mes) params.set('mes', filters.mes);
    const qs = params.toString();
    const res = await http.get<{ success: boolean; data: Record<string, { nombre: string; ordenes: number; ventas: number; ultima: string }> }>(`/dtf/clientes/stats${qs ? '?' + qs : ''}`);
    return res.data.data;
  },
  getClientes: async () => {
    const res = await http.get<ApiResponse<{ telefono: string; nombre: string; lugar: string }[]>>('/dtf/clientes');
    return extractData(res);
  },
  getFormatos: async () => {
    const res = await http.get<ApiResponse<{ formato: string; precioDefault: number }[]>>('/dtf/formatos');
    return extractData(res);
  },
  getOrdenes: async (filters?: Record<string, string>) => {
    const params = new URLSearchParams(filters || {});
    const res = await http.get<ApiResponse<unknown[]>>(`/dtf/ordenes?${params}`);
    return extractData(res);
  },
  getOrdenesCola: async () => {
    const res = await http.get<ApiResponse<unknown[]>>('/dtf/ordenes-cola');
    return extractData(res);
  },
  getOrdenesSheet: async (filters?: { ano?: string; mes?: string; dia?: string }) => {
    const params = new URLSearchParams();
    if (filters?.ano) params.set('ano', filters.ano);
    if (filters?.mes) params.set('mes', filters.mes);
    if (filters?.dia) params.set('dia', filters.dia);
    const res = await http.get<ApiResponse<unknown[]>>(`/dtf/ordenes-sheet?${params}`);
    return extractData(res);
  },
  getFechasDisponibles: async () => {
    const res = await http.get<ApiResponse<{ anos: string[]; mesesPorAno: Record<string, string[]>; diasPorMes: Record<string, string[]> }>>('/dtf/fechas-disponibles');
    return extractData(res);
  },
  crearOrden: async (data: Record<string, unknown>) => {
    const res = await http.post<ApiResponse<unknown>>('/dtf/ordenes', data);
    return extractData(res);
  },
  cambiarEstado: async (id: string, estado: Record<string, string>) => {
    const res = await http.patch<ApiResponse<unknown>>(`/dtf/ordenes/${id}/estado`, estado);
    return extractData(res);
  },
  eliminarOrden: async (id: string) => {
    await http.delete(`/dtf/ordenes/${id}`);
  },
  getDashboard: async (ano?: string, mes?: string, dia?: string) => {
    const params = new URLSearchParams();
    if (ano) params.set('ano', ano);
    if (mes) params.set('mes', mes);
    if (dia) params.set('dia', dia);
    const res = await http.get<ApiResponse<unknown>>(`/dtf/dashboard?${params}`);
    return extractData(res) as Record<string, unknown>;
  },
  getCuentasPorCobrar: async () => {
    const res = await http.get('/dtf/cuentas-por-cobrar');
    return res.data as unknown as { data: unknown[]; meta: { clientes: number; ordenes: number; total: number } };
  },
  pagarOrdenes: async (ids: string[]) => {
    const res = await http.post('/dtf/cuentas-por-cobrar/pagar', { ids });
    return (res.data as unknown as { data: { pagados: number } }).data;
  },
  pagarMonto: async (telefono: string, monto: number) => {
    const res = await http.post('/dtf/cuentas-por-cobrar/pagar-monto', { telefono, monto });
    return (res.data as unknown as { data: { ordenesPagadas: number; montoCubierto: number; cambio: number; ordenesRestantes: number } }).data;
  },
  getInventario: async () => {
    const res = await http.get<ApiResponse<unknown[]>>('/dtf/inventario');
    return extractData(res);
  },
};

// ─── WhatsApp ─────────────────────────────────────────────
export const whatsappApi = {
  send: async (telefono: string, mensaje: string) => {
    const res = await http.post('/whatsapp/send', { telefono, mensaje });
    return (res.data as unknown as { data: { success: boolean; messageId?: string; error?: string } }).data;
  },
  notify: async (telefono: string, tipo: string, data: { nombre: string; formato: string; cantidad: number; monto: number }) => {
    const res = await http.post('/whatsapp/notify', { telefono, tipo, ...data });
    return (res.data as unknown as { data: { success: boolean; messageId?: string; mensaje?: string; error?: string } }).data;
  },
  status: async () => {
    const res = await http.get('/whatsapp/status');
    return (res.data as unknown as { data: { connected: boolean } }).data;
  },
};

// ─── Users ────────────────────────────────────────────────
export const usersApi = {
  getAll: async () => { const r = await http.get<ApiResponse<unknown[]>>('/users'); return extractData(r); },
  create: async (data: { email: string; password: string; name: string; role: string }) => {
    const r = await http.post<ApiResponse<unknown>>('/users', data); return extractData(r);
  },
  update: async (id: string, data: Record<string, string>) => {
    const r = await http.put<ApiResponse<unknown>>(`/users/${id}`, data); return extractData(r);
  },
  delete: async (id: string) => { await http.delete(`/users/${id}`); },
  getPermissions: async () => {
    const r = await http.get<ApiResponse<{ role: string; modules: string[]; showPhones: boolean; canCreateOrders: boolean; canManageUsers: boolean; showAgentesIA: boolean }>>('/users/permissions');
    return extractData(r);
  },
};

export const colaTpuApi = {
  list: async () => { const res = await http.get('/cola-tpu'); return (res.data as unknown as { data: unknown[] }).data; },
  add: async (body: { tamano: string; cantidad: number; link: string }) => { const res = await http.post('/cola-tpu', body); return (res.data as unknown as { data: unknown }).data; },
  setEstado: async (id: string, estado: string) => { const res = await http.put('/cola-tpu/' + id, { estado }); return (res.data as unknown as { data: unknown }).data; },
  remove: async (id: string) => { const res = await http.delete('/cola-tpu/' + id); return (res.data as unknown as { data: unknown }).data; },
  downloadFile: async (id: string, type: 'psd' | 'pdf') => {
    const res = await http.get('/cola-tpu/' + id + '/files/' + type, { responseType: 'blob' });
    const blob = res.data as Blob; const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = type === 'psd' ? 'montaje.psd' : 'corte.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

export type PreciosEspeciales = {
  metro?: number; a4?: number; a4plus?: number; a3?: number; a3plus?: number;
  a4uv?: number; a3uv?: number; mediometro_uv?: number; cm63_uv?: number; metro_uv?: number;
  tpu57x21?: number; tpu57x33?: number; tpu57x53?: number; tpu57x103?: number;
};

export type ClienteDB = {
  telefono: string; nombre: string; lugar: string;
  direccion?: string; cedula?: string; correo?: string; nombreFacturacion?: string;
  esCredito: boolean; requiereFactura: boolean; esEncomienda: boolean;
  encomienda?: { entregarEn?: string; destino?: string; nombrePersona?: string; telefonoPersona?: string; envia?: string; paga?: string };
  preciosEspeciales?: PreciosEspeciales;
  notas?: string; createdAt: string; updatedAt: string;
};

export type ClienteLogEntry = {
  id: number; telefono: string; fecha: string; usuarioEmail: string; usuarioNombre: string;
  accion: 'crear' | 'actualizar' | 'borrar' | 'migrado';
  cambios: { campo: string; antes: unknown; despues: unknown }[];
};

export const clientesApi = {
  list: async (q?: string): Promise<ClienteDB[]> => {
    const res = await http.get('/clientes' + (q ? ('?q=' + encodeURIComponent(q)) : ''));
    return (res.data as unknown as { data: ClienteDB[] }).data;
  },
  get: async (tel: string): Promise<ClienteDB | null> => {
    try {
      const res = await http.get('/clientes/' + encodeURIComponent(tel));
      return (res.data as unknown as { data: ClienteDB }).data;
    } catch { return null; }
  },
  create: async (cli: Partial<ClienteDB> & { telefono: string }): Promise<ClienteDB> => {
    const res = await http.post('/clientes', cli);
    return (res.data as unknown as { data: ClienteDB }).data;
  },
  update: async (tel: string, patch: Partial<ClienteDB>): Promise<ClienteDB> => {
    const res = await http.put('/clientes/' + encodeURIComponent(tel), patch);
    return (res.data as unknown as { data: ClienteDB }).data;
  },
  remove: async (tel: string): Promise<void> => {
    await http.delete('/clientes/' + encodeURIComponent(tel));
  },
  log: async (tel: string): Promise<ClienteLogEntry[]> => {
    const res = await http.get('/clientes/' + encodeURIComponent(tel) + '/log');
    return (res.data as unknown as { data: ClienteLogEntry[] }).data;
  },
  count: async (): Promise<{ total: number; conPrecioEspecial: number }> => {
    const res = await http.get('/clientes/count');
    return (res.data as unknown as { data: { total: number; conPrecioEspecial: number } }).data;
  },
};


export const reportsApi = {
  getDashboard: async (): Promise<DashboardStats> => {
    const res = await http.get<ApiResponse<DashboardStats>>('/reports/dashboard');
    return extractData(res);
  },
  getByAgent: async (id: string): Promise<Task[]> => {
    const res = await http.get<ApiResponse<Task[]>>(`/reports/by-agent/${id}`);
    return extractData(res);
  },
  getByDate: async (filters?: TaskFilters): Promise<Task[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) {
          params.set(k, String(v));
        }
      });
    }
    const res = await http.get<ApiResponse<Task[]>>(`/reports/by-date?${params.toString()}`);
    return extractData(res);
  },
  exportCSV: (filters?: TaskFilters): string => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) params.set(k, String(v));
      });
    }
    // Obtener token para URL directa
    const stored = localStorage.getItem('auth-storage');
    let token = '';
    if (stored) {
      try {
        const { state } = JSON.parse(stored) as { state: { token: string } };
        token = state.token;
      } catch { /* ignore */ }
    }
    params.set('token', token);
    return `${API_BASE}/reports/export/csv?${params.toString()}`;
  },
  exportJSON: (filters?: TaskFilters): string => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) params.set(k, String(v));
      });
    }
    const stored = localStorage.getItem('auth-storage');
    let token = '';
    if (stored) {
      try {
        const { state } = JSON.parse(stored) as { state: { token: string } };
        token = state.token;
      } catch { /* ignore */ }
    }
    params.set('token', token);
    return `${API_BASE}/reports/export/json?${params.toString()}`;
  },
  exportPDF: (filters?: TaskFilters): string => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== '' && v !== null) params.set(k, String(v));
      });
    }
    const stored = localStorage.getItem('auth-storage');
    let token = '';
    if (stored) {
      try {
        const { state } = JSON.parse(stored) as { state: { token: string } };
        token = state.token;
      } catch { /* ignore */ }
    }
    params.set('token', token);
    return `${API_BASE}/reports/export/pdf?${params.toString()}`;
  },
};
